import { SocialSentiment } from 'api/requests/socialSentiments/interfaces';

export type TabIdentifier = string;

export interface CategoryTabInfo {
  key: TabIdentifier;
  label: string;
}

const formatCategoryLabel = (value: string): string => {
  const normalized = value.replace(/[_-]+/g, ' ').trim();
  if (!normalized) {
    return 'Uncategorized';
  }

  return normalized
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Maps category string to tab identifier and label.
 * Uses the category value directly from the backend.
 *
 * @param category - The category string from the sentiment
 * @returns The tab identifier (normalized category name) and label
 */
export const mapCategoryToTab = (category: string): CategoryTabInfo => {
  const categoryValue = typeof category === 'string' ? category.trim() : '';
  if (!categoryValue) {
    return {
      key: 'uncategorized',
      label: 'Uncategorized',
    };
  }

  // Normalize for grouping, but keep label as provided by backend
  const categoryNormalized = categoryValue.toLowerCase();

  return {
    key: categoryNormalized,
    label: formatCategoryLabel(categoryValue),
  };
};

/**
 * Gets the display title for a social sentiment.
 * Falls back to summary_md (first sentence or first 100 chars) when title is missing.
 */
export const getSocialSentimentTitle = (sentiment: SocialSentiment): string => {
  const rawTitle =
    typeof sentiment?.title === 'string' ? sentiment.title.trim() : '';
  const rawSummary = sentiment?.summary_md;

  return (
    rawTitle ||
    (typeof rawSummary === 'string' && rawSummary
      ? rawSummary.split('.')[0].trim().substring(0, 100) || 'Social Sentiment'
      : 'Social Sentiment')
  );
};
